// [EOM-XLSX 2026-10-08, user] "Download Excel" for the EOM Dashboard: one workbook, three tabs
// (Overall / USA / ROW), each a LIGHT-theme copy of its Dashboard row — the green board with the
// four tiles, the "Summary" legend + bar chart and the Provisional Inventory strip on the left,
// the Summary table on the right.
//
// A pure RENDERER. Every figure, label and percentage arrives already computed by EOMPage.tsx from
// the SAME helpers the Dashboard draws with, so the file cannot disagree with the screen.
//
// Built on the app's own dependency-free writer (utils/xlsx.ts: zip + esc). This file adds what that
// one-sheet writer never needed: cell styles, merged cells, row heights, rich-text titles and
// native Excel charts.
//
// ⭐ The chart is SIX small charts, one per pair, because the Dashboard scales each pair to its own
// size ([PER-PAIR SCALE 2026-09-03]) — one shared Excel axis would flatten $700 next to $220,349.
// Each chart's axis tops out at pairMax / BAR_FULL, the Dashboard's own headroom factor, so the
// taller bar of every pair stands at the same 60% height it does on screen.
import { colName, esc, zip } from './xlsx';

export interface EomXlsxTableRow {
  label: string;
  /** A green-bar subtotal row: white ink, bold. */
  tot: boolean;
  /** PM - Retention %: the figures are percentages, not dollars. */
  pct: boolean;
  cur: number | null;
  was: number | null;
  diff: number | null;
  /** The Variance exactly as the table prints it (already rounded), in per cent; null = blank. */
  shownVar: number | null;
}

export interface EomXlsxSheet {
  /** Tab name: Overall / USA / ROW. */
  name: string;
  boardTitle: string;
  summaryTitle: string;
  kpis: { label: string; value: number; delta: string; tone: 'up' | 'down' | 'flat' }[];
  /** The chart's pairs; '|' in a label is its line break. */
  chart: { label: string; now: number; prev: number }[];
  provisional: string;
  /** Formatted by the page with the same formatter the strip uses. */
  provisionalValue: string;
  /** False when last month has no figures at all — the Last Month column then reads "—". */
  hasPrev: boolean;
  table: EomXlsxTableRow[];
}

export interface EomXlsxSpec {
  monthTitle: string;
  lastMonthTitle: string;
  sheets: EomXlsxSheet[];
}

/* ── Look: the LIGHT theme's tokens (styles/theme.ts), flattened to solid colours ─────────── */

const C = {
  band: '125F4B',    // greenPanel — board band, green-bar rows, provisional strip, table title
  deep: '0F4F3E',    // greenPanelDeep — the table's column-heading row
  bar: '1A7F64',     // green600 — this month's bars
  barPrev: 'B4B5B5', // rgba(22,25,23,0.32) on white — last month's bars
  ink: '16735A',     // greenInk — tile values, a positive Variance, this month's bar labels
  text: '1A211E',    // text
  red: 'C0392B',     // the page's red for a negative / a fall
  line: 'D7DAD9',    // line — card and table borders
  soft: 'D4E2DF',    // rgba(255,255,255,0.82) on the band — "- The Scale Agency"
  zebra: 'FAFBFA',   // rgba(22,25,23,0.018) on white — every second table row
  white: 'FFFFFF',
};
/** The Dashboard chart's BAR_FULL: the taller bar of a pair fills this share of the plot. */
const BAR_FULL = 0.6;

/** Whole dollars, no minus sign: the page prints a negative as a plain amount in red. */
const MONEY0 = '"$"#,##0;"$"#,##0';
/** The tiles' fmtCurrency: cents, and "$-1,234.56" for a negative. */
const MONEY2 = '"$"#,##0.00;"$-"#,##0.00';
const PCT2 = '0.00%';
const PCT2_SIGNED = '+0.00%;-0.00%;+0.00%';
const VAR0 = '0%';

/* Grid. Columns (0-based): A margin · B band padding · four tiles of three columns with a gap
   column between them · R band padding · S gap · T..X the Summary table · Y margin.
   [VISIBLE 2026-10-08, user] "all the numbers should be properly visible": tile columns 11 wide make each
   of the six charts ~160 px, so both value labels sit side by side on one line (at 9 wide they were 100 pt
   and Excel wrapped "$58,234" over three lines); the table columns keep headroom over their longest text
   ("CURRENT MONTH (SEPTEMBER 2026)" had under 2 pt to spare at 27). Checked in Excel: no ####, every text fits. */
const COLS = [1.7, 1.7, 11, 11, 11, 1.7, 11, 11, 11, 1.7, 11, 11, 11, 1.7, 11, 11, 11, 1.7, 3, 30, 31, 31, 15, 13, 1.7];
const B_LEFT = 1;
const B_RIGHT = 17;
const TILE_COLS = [2, 6, 10, 14];
const T_LEFT = 19;
const T_RIGHT = 23;
/* Rows (1-based). Board and table share rows, so every row the table uses is one table-row tall:
   2 titles · 3 tile labels / column headings · 4-5 tiles / data · 6 band foot · 8 legend ·
   9-23 chart · 25 provisional strip / the table's last row (22 data rows = 4..25). */
const LAST_ROW = 26;
const ROW_H: Record<number, number> = { 1: 8, 2: 27, 3: 18, [LAST_ROW]: 8 };
const ROW_DEFAULT = 21;
const CHART_TOP = 9;
const CHART_BOTTOM = 23;

/* ── Styles ────────────────────────────────────────────────────────────────────────────────── */

interface Style {
  b?: boolean;
  sz?: number;
  color?: string;
  fill?: string;
  /** Thin border colours per edge. */
  bl?: string;
  br?: string;
  bt?: string;
  bb?: string;
  fmt?: string;
  h?: 'left' | 'center' | 'right';
  v?: 'top' | 'center' | 'bottom';
  indent?: number;
}

type Run = { text: string; b?: boolean; sz?: number; color?: string };
type Val = number | string | Run[] | null;

/** Escape for an XML attribute value (esc() leaves quotes alone; number formats carry them). */
const attr = (s: string) => esc(s).replace(/"/g, '&quot;');

/** Deduplicates fonts / fills / borders / number formats / cell formats as cells ask for them. */
class StyleBook {
  private fonts: string[] = ['<font><sz val="11"/><color rgb="FF1A211E"/><name val="Calibri"/><family val="2"/></font>'];
  private fills: string[] = ['<fill><patternFill patternType="none"/></fill>', '<fill><patternFill patternType="gray125"/></fill>'];
  private borders: string[] = ['<border><left/><right/><top/><bottom/><diagonal/></border>'];
  private fmts: string[] = [];
  private xfs: string[] = ['<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'];
  private ids = new Map<string, number>();

  private index(list: string[], xml: string): number {
    const i = list.indexOf(xml);
    if (i >= 0) return i;
    list.push(xml);
    return list.length - 1;
  }

  id(s: Style): number {
    const key = JSON.stringify(s);
    const hit = this.ids.get(key);
    if (hit !== undefined) return hit;
    const font = this.index(this.fonts,
      `<font>${s.b ? '<b/>' : ''}<sz val="${s.sz ?? 10}"/><color rgb="FF${s.color ?? C.text}"/><name val="Calibri"/><family val="2"/></font>`);
    const fill = s.fill
      ? this.index(this.fills, `<fill><patternFill patternType="solid"><fgColor rgb="FF${s.fill}"/><bgColor indexed="64"/></patternFill></fill>`)
      : 0;
    const edge = (tag: string, color?: string) => (color ? `<${tag} style="thin"><color rgb="FF${color}"/></${tag}>` : `<${tag}/>`);
    const border = s.bl || s.br || s.bt || s.bb
      ? this.index(this.borders, `<border>${edge('left', s.bl)}${edge('right', s.br)}${edge('top', s.bt)}${edge('bottom', s.bb)}<diagonal/></border>`)
      : 0;
    const fmt = s.fmt ? 164 + this.index(this.fmts, s.fmt) : 0;
    const align = `<alignment${s.h ? ` horizontal="${s.h}"` : ''} vertical="${s.v ?? 'center'}"${s.indent ? ` indent="${s.indent}"` : ''}/>`;
    this.xfs.push(`<xf numFmtId="${fmt}" fontId="${font}" fillId="${fill}" borderId="${border}" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1">${align}</xf>`);
    const id = this.xfs.length - 1;
    this.ids.set(key, id);
    return id;
  }

  xml(): string {
    return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
      + '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
      + (this.fmts.length
        ? `<numFmts count="${this.fmts.length}">${this.fmts.map((f, i) => `<numFmt numFmtId="${164 + i}" formatCode="${attr(f)}"/>`).join('')}</numFmts>`
        : '')
      + `<fonts count="${this.fonts.length}">${this.fonts.join('')}</fonts>`
      + `<fills count="${this.fills.length}">${this.fills.join('')}</fills>`
      + `<borders count="${this.borders.length}">${this.borders.join('')}</borders>`
      + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
      // ⚠ count must equal the number of <xf> entries or Excel refuses the file (memory: inventory-export-xlsx).
      + `<cellXfs count="${this.xfs.length}">${this.xfs.join('')}</cellXfs>`
      + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
      + '</styleSheet>';
  }
}

/* ── One sheet's cells ─────────────────────────────────────────────────────────────────────── */

interface Cell { v: Val; s: Style }

class Grid {
  rows = new Map<number, Map<number, Cell>>();
  merges: string[] = [];

  at(r: number, c: number): Cell {
    let row = this.rows.get(r);
    if (!row) { row = new Map(); this.rows.set(r, row); }
    let cell = row.get(c);
    if (!cell) { cell = { v: null, s: {} }; row.set(c, cell); }
    return cell;
  }

  set(r: number, c: number, v: Val, s: Style = {}): void {
    const cell = this.at(r, c);
    cell.v = v;
    Object.assign(cell.s, s);
  }

  /** Add style properties to every cell of a range (merged cells need their style on every cell). */
  paint(r1: number, c1: number, r2: number, c2: number, s: Style): void {
    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) Object.assign(this.at(r, c).s, s);
  }

  merge(r1: number, c1: number, r2: number, c2: number): void {
    this.paint(r1, c1, r2, c2, {});
    this.merges.push(`${colName(c1)}${r1}:${colName(c2)}${r2}`);
  }

  /** A thin outline around a range. */
  box(r1: number, c1: number, r2: number, c2: number, color: string): void {
    for (let c = c1; c <= c2; c++) { this.at(r1, c).s.bt = color; this.at(r2, c).s.bb = color; }
    for (let r = r1; r <= r2; r++) { this.at(r, c1).s.bl = color; this.at(r, c2).s.br = color; }
  }
}

function cellXml(ref: string, v: Val, styleId: number): string {
  const s = styleId ? ` s="${styleId}"` : '';
  if (typeof v === 'number' && Number.isFinite(v)) return `<c r="${ref}"${s}><v>${v}</v></c>`;
  if (Array.isArray(v)) {
    const runs = v.map((run) => `<r><rPr>${run.b ? '<b/>' : ''}<sz val="${run.sz ?? 10}"/><color rgb="FF${run.color ?? C.text}"/>`
      + `<rFont val="Calibri"/><family val="2"/></rPr><t xml:space="preserve">${esc(run.text)}</t></r>`).join('');
    return `<c r="${ref}"${s} t="inlineStr"><is>${runs}</is></c>`;
  }
  if (v == null || v === '') return `<c r="${ref}"${s}/>`;
  return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${esc(String(v))}</t></is></c>`;
}

function sheetXml(g: Grid, book: StyleBook, first: boolean): string {
  const rows: string[] = [];
  for (let r = 1; r <= LAST_ROW; r++) {
    const row = g.rows.get(r);
    const cells = (row ? Array.from(row.entries()) : []).sort((a, b) => a[0] - b[0])
      .map(([c, cell]) => cellXml(`${colName(c)}${r}`, cell.v, book.id(cell.s))).join('');
    rows.push(`<row r="${r}" ht="${ROW_H[r] ?? ROW_DEFAULT}" customHeight="1">${cells}</row>`);
  }
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" '
    + 'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
    + '<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>'
    + `<dimension ref="A1:${colName(COLS.length - 1)}${LAST_ROW}"/>`
    // No gridlines: the sheet should read as the Dashboard, not as a grid of cells. Every tab opens at 100% (user, 10-08).
    + `<sheetViews><sheetView workbookViewId="0" showGridLines="0" zoomScale="100" zoomScaleNormal="100"${first ? ' tabSelected="1"' : ''}/></sheetViews>`
    + `<sheetFormatPr defaultRowHeight="${ROW_DEFAULT}" customHeight="1"/>`
    + `<cols>${COLS.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('')}</cols>`
    + `<sheetData>${rows.join('')}</sheetData>`
    + `<mergeCells count="${g.merges.length}">${g.merges.map((m) => `<mergeCell ref="${m}"/>`).join('')}</mergeCells>`
    + '<printOptions horizontalCentered="1"/>'
    + '<pageMargins left="0.3" right="0.3" top="0.4" bottom="0.4" header="0.2" footer="0.2"/>'
    + '<pageSetup orientation="landscape" fitToWidth="1" fitToHeight="1"/>'
    + '<drawing r:id="rId1"/>'
    + '</worksheet>';
}

/* ── Layout: one Dashboard row ─────────────────────────────────────────────────────────────── */

function layout(sh: EomXlsxSheet, spec: EomXlsxSpec): Grid {
  const g = new Grid();

  // The green board: title, then the four tiles on the band.
  g.paint(2, B_LEFT, 6, B_RIGHT, { fill: C.band });
  g.merge(2, B_LEFT, 2, B_RIGHT);
  g.set(2, B_LEFT, [
    { text: sh.boardTitle, b: true, sz: 12, color: C.white },
    { text: ' - The Scale Agency', b: true, sz: 12, color: C.soft },
  ], { h: 'center' });
  sh.kpis.slice(0, TILE_COLS.length).forEach((k, i) => {
    const c0 = TILE_COLS[i];
    g.paint(3, c0, 5, c0 + 2, { fill: C.white });
    for (let r = 3; r <= 5; r++) g.merge(r, c0, r, c0 + 2);
    g.set(3, c0, k.label, { b: true, sz: 8, h: 'left', v: 'bottom', indent: 1 });
    g.set(4, c0, k.value, { b: true, sz: 15, color: C.ink, fmt: MONEY2, h: 'left', indent: 1 });
    g.set(5, c0, k.delta, { b: true, sz: 8, color: k.tone === 'up' ? C.ink : k.tone === 'down' ? C.red : C.text, h: 'left', v: 'top', indent: 1 });
  });

  // The chart card: legend row, then the six charts (drawn by the drawing part over rows 9-23).
  g.paint(8, B_LEFT, CHART_BOTTOM, B_RIGHT, { fill: C.white });
  g.box(8, B_LEFT, CHART_BOTTOM, B_RIGHT, C.line);
  g.merge(8, B_LEFT + 1, 8, B_RIGHT - 1);
  g.set(8, B_LEFT + 1, [
    { text: 'Summary', b: true, sz: 10 },
    { text: '        ' },
    { text: '■', sz: 12, color: C.bar },
    { text: ` Current Month (${spec.monthTitle})`, b: true, sz: 9 },
    { text: '        ' },
    { text: '■', sz: 12, color: C.barPrev },
    { text: ` Last Month (${spec.lastMonthTitle})`, b: true, sz: 9 },
  ], { h: 'left' });

  // The Provisional Inventory strip.
  g.paint(25, B_LEFT, 25, B_RIGHT, { fill: C.band });
  g.merge(25, B_LEFT, 25, B_RIGHT);
  g.set(25, B_LEFT, `${sh.provisional}   ${sh.provisionalValue}`, { b: true, sz: 11, color: C.white, h: 'center' });

  // The Summary table: title, column headings, 22 rows.
  g.paint(2, T_LEFT, 2, T_RIGHT, { fill: C.band });
  g.merge(2, T_LEFT, 2, T_RIGHT);
  g.set(2, T_LEFT, [
    { text: sh.summaryTitle, b: true, sz: 12, color: C.white },
    { text: ' | The Scale Agency', b: true, sz: 12, color: C.soft },
  ], { h: 'center' });
  ['Particulars', `Current Month (${spec.monthTitle})`, `Last Month (${spec.lastMonthTitle})`, 'Difference', 'Variance']
    .forEach((h, i) => g.set(3, T_LEFT + i, h.toUpperCase(),
      { b: true, sz: 8, color: C.white, fill: C.deep, h: i ? 'right' : 'left', indent: 1 }));

  sh.table.forEach((row, i) => {
    const r = 4 + i;
    // Green-bar rows: white ink on the band. Others: zebra on every second row, as the page does.
    const base: Style = row.tot ? { fill: C.band, b: true, color: C.white } : (i % 2 === 1 ? { fill: C.zebra } : {});
    /** A dollar figure: red when negative, except on the green bar where it stays white. */
    const money = (v: number): Style => ({ ...base, sz: 10, fmt: MONEY0, h: 'right', indent: 1, ...(v < 0 && !row.tot ? { color: C.red } : {}) });
    const pctStyle = (fmt: string): Style => ({ ...base, sz: 10, fmt, h: 'right', indent: 1 });
    const dash: Style = { ...base, sz: 10, h: 'right', indent: 1 };

    g.set(r, T_LEFT, row.label, { ...base, sz: 10, h: 'left', indent: 1 });

    if (row.pct) {
      g.set(r, T_LEFT + 1, row.cur == null ? '—' : row.cur / 100, row.cur == null ? dash : pctStyle(PCT2));
      g.set(r, T_LEFT + 2, !sh.hasPrev || row.was == null ? '—' : row.was / 100, !sh.hasPrev || row.was == null ? dash : pctStyle(PCT2));
      g.set(r, T_LEFT + 3, row.diff == null ? '—' : row.diff / 100, row.diff == null ? dash : pctStyle(PCT2_SIGNED));
    } else {
      const cur = row.cur ?? 0;
      g.set(r, T_LEFT + 1, cur, money(cur));
      if (!sh.hasPrev) g.set(r, T_LEFT + 2, '—', dash);
      else { const was = row.was ?? 0; g.set(r, T_LEFT + 2, was, money(was)); }
      if (row.diff == null) g.set(r, T_LEFT + 3, '—', dash);
      else g.set(r, T_LEFT + 3, row.diff, money(row.diff));
    }

    // Variance: bold, coloured by its sign as shown; the green bar keeps white.
    const sv = row.shownVar;
    const tone = row.tot || sv == null || sv === 0 ? {} : { color: sv > 0 ? C.ink : C.red };
    g.set(r, T_RIGHT, sv == null ? null : sv / 100, { ...base, b: true, sz: 10, fmt: row.pct ? PCT2 : VAR0, h: 'right', indent: 1, ...tone });
  });
  g.box(2, T_LEFT, 25, T_RIGHT, C.line);
  return g;
}

/* ── Charts ────────────────────────────────────────────────────────────────────────────────── */

const num = (v: number) => (Number.isFinite(v) ? String(Math.round(v * 100) / 100) : '0');

/* `oneLine`: a value label must NEVER wrap — Excel 2013+ wraps data labels to a width it picks itself, which
   broke "$58,234" into "$58 / ,23 / 4" above the bars ([VISIBLE 2026-10-08]). */
const textProps = (sz: number, color: string, bold: boolean, oneLine = false) =>
  `<c:txPr>${oneLine ? '<a:bodyPr wrap="none" lIns="0" tIns="0" rIns="0" bIns="0" anchor="ctr"><a:spAutoFit/></a:bodyPr>' : '<a:bodyPr/>'}`
  + `<a:lstStyle/><a:p><a:pPr><a:defRPr sz="${sz}" b="${bold ? 1 : 0}"><a:solidFill><a:srgbClr val="${color}"/></a:solidFill>`
  + '<a:latin typeface="Calibri"/></a:defRPr></a:pPr><a:endParaRPr lang="en-US"/></a:p></c:txPr>';

function chartXml(pair: { label: string; now: number; prev: number }, spec: EomXlsxSpec): string {
  const max = Math.max(pair.now, pair.prev, 1) / BAR_FULL;
  const min = Math.min(0, pair.now, pair.prev);
  /* Every category label gets TWO lines: a one-line label ("PM Inventory") left that chart's plot taller, so its
     baseline sat lower than the other five. A non-breaking space as the second line keeps all six level. */
  const lines = pair.label.split('|');
  if (lines.length < 2) lines.push(' ');
  const cat = `<c:cat><c:strLit><c:ptCount val="1"/><c:pt idx="0"><c:v>${esc(lines.join('\n'))}</c:v></c:pt></c:strLit></c:cat>`;
  const ser = (idx: number, name: string, value: number, fill: string, labelColor: string) =>
    `<c:ser><c:idx val="${idx}"/><c:order val="${idx}"/><c:tx><c:v>${esc(name)}</c:v></c:tx>`
    + `<c:spPr><a:solidFill><a:srgbClr val="${fill}"/></a:solidFill><a:ln><a:noFill/></a:ln></c:spPr>`
    + '<c:invertIfNegative val="0"/>'
    + `<c:dLbls><c:numFmt formatCode="${attr('"$"#,##0')}" sourceLinked="0"/><c:spPr><a:noFill/><a:ln><a:noFill/></a:ln></c:spPr>`
    + `${textProps(900, labelColor, true, true)}<c:dLblPos val="outEnd"/><c:showLegendKey val="0"/><c:showVal val="1"/>`
    + '<c:showCatName val="0"/><c:showSerName val="0"/><c:showPercent val="0"/><c:showBubbleSize val="0"/></c:dLbls>'
    + cat
    + `<c:val><c:numLit><c:formatCode>General</c:formatCode><c:ptCount val="1"/><c:pt idx="0"><c:v>${num(value)}</c:v></c:pt></c:numLit></c:val>`
    + '</c:ser>';
  const noLine = '<c:spPr><a:noFill/><a:ln><a:noFill/></a:ln></c:spPr>';
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<c:chartSpace xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" '
    + 'xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" '
    + 'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
    + '<c:roundedCorners val="0"/>'
    + '<c:chart><c:autoTitleDeleted val="1"/><c:plotArea><c:layout/>'
    + '<c:barChart><c:barDir val="col"/><c:grouping val="clustered"/><c:varyColors val="0"/>'
    + ser(0, `Current Month (${spec.monthTitle})`, pair.now, C.bar, C.ink)
    + ser(1, `Last Month (${spec.lastMonthTitle})`, pair.prev, C.barPrev, C.text)
    // Space BETWEEN the pair's two bars (overlap -40) keeps their two labels apart — the Dashboard's LABEL_DX idea.
    + '<c:gapWidth val="60"/><c:overlap val="-40"/><c:axId val="5001"/><c:axId val="5002"/></c:barChart>'
    + '<c:catAx><c:axId val="5001"/><c:scaling><c:orientation val="minMax"/></c:scaling><c:delete val="0"/>'
    + '<c:axPos val="b"/><c:numFmt formatCode="General" sourceLinked="0"/><c:majorTickMark val="none"/>'
    + '<c:minorTickMark val="none"/><c:tickLblPos val="low"/>'
    + `<c:spPr><a:ln w="9525"><a:solidFill><a:srgbClr val="${C.line}"/></a:solidFill></a:ln></c:spPr>`
    + `${textProps(900, C.text, true)}<c:crossAx val="5002"/><c:crosses val="autoZero"/><c:auto val="1"/>`
    + '<c:lblAlgn val="ctr"/><c:lblOffset val="100"/><c:noMultiLvlLbl val="0"/></c:catAx>'
    + `<c:valAx><c:axId val="5002"/><c:scaling><c:orientation val="minMax"/><c:max val="${num(max)}"/><c:min val="${num(min)}"/></c:scaling>`
    + '<c:delete val="1"/><c:axPos val="l"/><c:numFmt formatCode="General" sourceLinked="0"/><c:majorTickMark val="none"/>'
    + '<c:minorTickMark val="none"/><c:tickLblPos val="nextTo"/><c:crossAx val="5001"/><c:crosses val="autoZero"/>'
    + '<c:crossBetween val="between"/></c:valAx>'
    + `${noLine}</c:plotArea><c:plotVisOnly val="1"/><c:dispBlanksAs val="gap"/></c:chart>`
    + `${noLine}${textProps(900, C.text, false)}`
    + '</c:chartSpace>';
}

/** Six equal slots across the chart card, as two-cell anchors (column + EMU offset). */
function chartAnchors(n: number): { c1: number; o1: number; c2: number; o2: number }[] {
  // Excel's own column-width → pixel rule for Calibri 11 (maximum digit width 7 px).
  const px = COLS.map((w) => Math.trunc(((256 * w + Math.trunc(128 / 7)) / 256) * 7));
  const xOf = (c: number) => px.slice(0, c).reduce((a, b) => a + b, 0);
  const toCell = (x: number) => {
    let c = 0;
    while (c < px.length - 1 && xOf(c + 1) <= x) c++;
    return { c, o: Math.round((x - xOf(c)) * 9525) };
  };
  const left = xOf(B_LEFT + 1);
  const width = xOf(B_RIGHT) - left;
  return Array.from({ length: n }, (_, k) => {
    const a = toCell(left + (width * k) / n);
    const b = toCell(left + (width * (k + 1)) / n);
    return { c1: a.c, o1: a.o, c2: b.c, o2: b.o };
  });
}

function drawingXml(n: number): string {
  const anchors = chartAnchors(n).map((p, k) =>
    '<xdr:twoCellAnchor editAs="oneCell">'
    + `<xdr:from><xdr:col>${p.c1}</xdr:col><xdr:colOff>${p.o1}</xdr:colOff><xdr:row>${CHART_TOP - 1}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from>`
    + `<xdr:to><xdr:col>${p.c2}</xdr:col><xdr:colOff>${p.o2}</xdr:colOff><xdr:row>${CHART_BOTTOM}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to>`
    + `<xdr:graphicFrame macro=""><xdr:nvGraphicFramePr><xdr:cNvPr id="${k + 2}" name="Chart ${k + 1}"/>`
    + '<xdr:cNvGraphicFramePr><a:graphicFrameLocks noGrp="1"/></xdr:cNvGraphicFramePr></xdr:nvGraphicFramePr>'
    + '<xdr:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/></xdr:xfrm>'
    + '<a:graphic><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart">'
    + '<c:chart xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" '
    + `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="rId${k + 1}"/>`
    + '</a:graphicData></a:graphic></xdr:graphicFrame><xdr:clientData/></xdr:twoCellAnchor>').join('');
  return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" '
    + `xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">${anchors}</xdr:wsDr>`;
}

/* ── Package ───────────────────────────────────────────────────────────────────────────────── */

const REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const rels = (list: [string, string, string][]) =>
  '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
  + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
  + list.map(([id, type, target]) => `<Relationship Id="${id}" Type="${REL}/${type}" Target="${target}"/>`).join('')
  + '</Relationships>';

/** The whole workbook: one tab per sheet in `spec.sheets`, in order. */
export function buildEomDashboardXlsx(spec: EomXlsxSpec): Uint8Array<ArrayBuffer> {
  const book = new StyleBook();
  const parts: { name: string; text: string }[] = [];
  const types: string[] = [];
  let chartNo = 0;

  spec.sheets.forEach((sh, i) => {
    const n = i + 1;
    parts.push({ name: `xl/worksheets/sheet${n}.xml`, text: sheetXml(layout(sh, spec), book, i === 0) });
    parts.push({ name: `xl/worksheets/_rels/sheet${n}.xml.rels`, text: rels([['rId1', 'drawing', `../drawings/drawing${n}.xml`]]) });
    const ids = sh.chart.map((pair) => {
      chartNo += 1;
      parts.push({ name: `xl/charts/chart${chartNo}.xml`, text: chartXml(pair, spec) });
      types.push(`<Override PartName="/xl/charts/chart${chartNo}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/>`);
      return chartNo;
    });
    parts.push({ name: `xl/drawings/drawing${n}.xml`, text: drawingXml(ids.length) });
    parts.push({
      name: `xl/drawings/_rels/drawing${n}.xml.rels`,
      text: rels(ids.map((id, k): [string, string, string] => [`rId${k + 1}`, 'chart', `../charts/chart${id}.xml`])),
    });
    types.push(`<Override PartName="/xl/worksheets/sheet${n}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`);
    types.push(`<Override PartName="/xl/drawings/drawing${n}.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>`);
  });

  const contentTypes = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
    + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
    + '<Default Extension="xml" ContentType="application/xml"/>'
    + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
    + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
    + `${types.join('')}</Types>`;
  const workbook = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
    + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" '
    + `xmlns:r="${REL}"><bookViews><workbookView activeTab="0"/></bookViews><sheets>`
    + spec.sheets.map((sh, i) => `<sheet name="${attr(sh.name.slice(0, 31))}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')
    + '</sheets></workbook>';
  const workbookRels = rels([
    ...spec.sheets.map((_, i): [string, string, string] => [`rId${i + 1}`, 'worksheet', `worksheets/sheet${i + 1}.xml`]),
    [`rId${spec.sheets.length + 1}`, 'styles', 'styles.xml'],
  ]);

  return zip([
    { name: '[Content_Types].xml', text: contentTypes },
    { name: '_rels/.rels', text: rels([['rId1', 'officeDocument', 'xl/workbook.xml']]) },
    { name: 'xl/workbook.xml', text: workbook },
    { name: 'xl/_rels/workbook.xml.rels', text: workbookRels },
    // Styles last: every sheet above has registered the formats it uses by now.
    { name: 'xl/styles.xml', text: book.xml() },
    ...parts,
  ]);
}
